import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./CoachingCheckout.css"; // reuse the shared checkout styles
import Navbar from "../components/Navbar";
import { supabase } from "../supabase";
import { SST_RATE, SST_LABEL } from "../constants";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { parsePhoneNumber } from "libphonenumber-js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

const ConfirmationModal = ({ isOpen, onClose, onConfirm }) => {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Confirm Order</h2>
        <div className="modal-body">
          <p>By clicking Agree, you confirm all items in your order are correct.</p>
          <p>
            <strong>Refund Policy (Last updated: 05/06/2025):</strong><br />
            - Cancel anytime as long as it is not within 48 hours before the activity time for a full refund as credits.<br />
            - No refunds within 48 hours of the activity time.<br />
            - Credits are non-transferable, non-redeemable for cash.<br />
            - No cash refunds.<br />
            - For issues, contact <a href="mailto:info@pickledepot.com.my">info@pickledepot.com.my</a>.<br />
            Full Terms: <a href="https://www.pickledepot.com.my/_files/ugd/01db86_19a89a1d4ff843b39265478a620528a0.pdf" target="_blank" rel="noopener noreferrer">here</a>.
          </p>
        </div>
        <div className="modal-buttons">
          <button className="modal-close-button" onClick={onClose} aria-label="Cancel order confirmation">Cancel</button>
          <button className="modal-confirm-button" onClick={onConfirm} aria-label="Agree to terms and confirm order"> Agree</button>
        </div>
      </div>
    </div>
  );
};

const ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im12dmhycGZhY2dtbmZpZ254dGtiIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDUwNjIzNjAsImV4cCI6MjA2MDYzODM2MH0.mHAiPSOC3pS5vTBcQB-__TsziwT30gnyI9aVuxeSazw';

const formatActivityDate = (dateStr) => {
  const [y, m, d] = (dateStr || '').split('-').map(Number);
  if (!y) return dateStr;
  return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString('en-MY', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

const ActivityCheckout = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { activity, seats: seatsRaw } = location.state || {};
  const seats = Number(seatsRaw) || 1;
  // sports.name of the activity being bought ("Padel" / "Pickle"), forwarded by
  // ActivityForm since 2026-09-08. The fallback keeps a checkout that was already
  // in flight on the previous bundle (state without sport_name, Padel-only then)
  // working, and is all the no-activity bounce below has to go on.
  const sportName = activity?.sport_name || "Padel";

  const perSeat = Number(activity?.price) || 0;

  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [availableCredits, setAvailableCredits] = useState(0);
  const [useCredits, setUseCredits] = useState(true);
  const [creditsToUse, setCreditsToUse] = useState(0);
  const [discountedTotal, setDiscountedTotal] = useState(0);
  const [finalTotal, setFinalTotal] = useState(0);
  const [notification, setNotification] = useState({ message: "", show: false });
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeApplied, setPromoCodeApplied] = useState(null);
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [isPromoLoading, setIsPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState({ billingName: "", billingEmail: "", billingMobile: "" });
  const [formErrors, setFormErrors] = useState({});
  const paymentFormRef = useRef(null);
  const phoneInputRef = useRef(null);
  const phoneInputContainerRef = useRef(null);

  const fixedBillingAddress = "WS4, Sentul Depot, Jalan Strachan, Sentul West, 51100 Kuala Lumpur, Wilayah Persekutuan Kuala Lumpur";
  const memoizedLocationState = useMemo(() => location.state, [location.state]);

  const isSafari = () => {
    const ua = navigator.userAgent.toLowerCase();
    return ua.includes("safari") && !ua.includes("chrome");
  };

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validatePhoneNumber = (value) => {
    if (!value) return "Billing Mobile is required";
    try {
      const phoneNumber = parsePhoneNumber(value);
      if (!phoneNumber || !phoneNumber.isValid()) return "Invalid phone number (e.g., +12025550123)";
      return null;
    } catch (error) {
      return "Invalid phone number format";
    }
  };

  const debouncedValidate = useRef(
    debounce((name, value) => {
      let normalizedValue = value;
      if (name === "billingMobile" && value) {
        try {
          const phoneNumber = parsePhoneNumber(value);
          if (phoneNumber?.isValid()) normalizedValue = phoneNumber.formatInternational();
        } catch {}
      }
      setFormErrors((prev) => {
        const errors = { ...prev };
        if (!normalizedValue || normalizedValue.trim() === "") {
          errors[name] = `${name.replace("billing", "Billing ")} is required`;
        } else {
          delete errors[name];
        }
        if (name === "billingName" && normalizedValue) {
          if (!/^[a-zA-Z0-9\s]+$/.test(normalizedValue)) errors.billingName = "Billing Name must be alphanumeric";
          else if (normalizedValue.length > 128) errors.billingName = "Billing Name must be up to 128 characters";
        }
        if (name === "billingEmail" && normalizedValue) {
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedValue)) errors.billingEmail = "Invalid email format";
          else if (normalizedValue.length > 128) errors.billingEmail = "Billing Email must be up to 128 characters";
        }
        if (name === "billingMobile" && normalizedValue) {
          const phoneError = validatePhoneNumber(normalizedValue);
          if (phoneError) errors.billingMobile = phoneError;
          else delete errors.billingMobile;
        }
        return errors;
      });
    }, 300)
  ).current;

  // Guard: no activity in navigation state → bounce back to the list.
  useEffect(() => {
    if (!activity || !activity.id) {
      navigate("/", { state: { selectedBookingType: "Bookings", selectedSport: `${sportName} Activities` } });
    }
  }, [activity, sportName, navigate]);

  // Auth check and profile/credits fetch + billing autofill from latest order.
  useEffect(() => {
    const checkSession = async () => {
      let hasRedirected = false;
      const redirectToLogin = () => {
        if (!hasRedirected) {
          hasRedirected = true;
          navigate("/login", { state: { from: location.pathname, ...memoizedLocationState } });
        }
      };
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error || !session) { redirectToLogin(); return; }
        setIsAuthenticated(true);
        const { data: user, error: userError } = await supabase.auth.getUser();
        if (userError || !user) { redirectToLogin(); return; }

        const { data: creditsData, error: creditsError } = await supabase
          .from("credits").select("balance").eq("user_id", user.user.id).single();
        if (creditsError) {
          if (creditsError.code === "PGRST116") setAvailableCredits(0);
          else { redirectToLogin(); return; }
        } else {
          setAvailableCredits(creditsData?.balance || 0);
        }

        const { data: orderData, error: orderError } = await supabase
          .from("orders").select("billing_name, billing_email, billing_mobile")
          .eq("user_id", user.user.id).order("created_at", { ascending: false }).limit(1).single();
        if (orderError && orderError.code !== "PGRST116") {
          console.error("ActivityCheckout.js: Error fetching latest order:", orderError);
        } else if (orderData) {
          setFormData({
            billingName: orderData.billing_name || "",
            billingEmail: orderData.billing_email || "",
            billingMobile: orderData.billing_mobile || "",
          });
        }
      } catch (error) {
        console.error("ActivityCheckout.js: Unexpected error in checkSession:", error);
        redirectToLogin();
      } finally {
        setIsLoading(false);
      }
    };
    checkSession();
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT" || !session) {
        navigate("/login", { state: { from: location.pathname, ...memoizedLocationState } });
      }
    });
    return () => authListener.subscription.unsubscribe();
  }, [navigate, location.pathname, memoizedLocationState]);

  // Poll for autofill (Safari + others).
  useEffect(() => {
    if (!phoneInputRef.current) return;
    const checkForAutofill = () => {
      const input = phoneInputRef.current;
      if (!input) return;
      if (input.value && input.value !== formData.billingMobile) {
        handleChange("billingMobile", input.value || "");
        debouncedValidate("billingMobile", input.value || "");
      }
      const billingNameInput = document.getElementById("billingName");
      if (billingNameInput && billingNameInput.value && billingNameInput.value !== formData.billingName) {
        handleChange("billingName", billingNameInput.value);
        debouncedValidate("billingName", billingNameInput.value);
      }
      const billingEmailInput = document.getElementById("billingEmail");
      if (billingEmailInput && billingEmailInput.value && billingEmailInput.value !== formData.billingEmail) {
        handleChange("billingEmail", billingEmailInput.value);
        debouncedValidate("billingEmail", billingEmailInput.value);
      }
    };
    const interval = setInterval(checkForAutofill, isSafari() ? 100 : 500);
    const timeout = setTimeout(() => clearInterval(interval), 5000);
    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [formData.billingMobile, formData.billingName, formData.billingEmail]);

  // Recalculate credits to use.
  useEffect(() => {
    if (useCredits) {
      setCreditsToUse(Math.min(availableCredits, Math.max(0, discountedTotal - promoDiscount)));
    } else {
      setCreditsToUse(0);
    }
  }, [availableCredits, discountedTotal, useCredits, promoDiscount]);

  // Recalculate totals.
  useEffect(() => {
    const total = perSeat * seats;
    setDiscountedTotal(total);
    setFinalTotal(Math.max(0, total - promoDiscount - (useCredits ? creditsToUse : 0)));
  }, [perSeat, seats, useCredits, creditsToUse, promoDiscount]);

  const handlePhoneBlur = () => {
    if (phoneInputRef.current) {
      const inputValue = phoneInputRef.current.value;
      if (inputValue && inputValue !== formData.billingMobile) {
        handleChange("billingMobile", inputValue || "");
        debouncedValidate("billingMobile", inputValue || "");
      }
    }
  };

  const validateForm = () => {
    const errors = {};
    Object.keys(formData).forEach((name) => {
      if (!formData[name] || formData[name].trim() === "") {
        errors[name] = `${name.replace("billing", "Billing ")} is required`;
      }
      if (name === "billingName" && formData[name]) {
        if (!/^[a-zA-Z0-9\s]+$/.test(formData[name])) errors.billingName = "Billing Name must be alphanumeric";
        else if (formData[name].length > 128) errors.billingName = "Billing Name must be up to 128 characters";
      }
      if (name === "billingEmail" && formData[name]) {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData[name])) errors.billingEmail = "Invalid email format";
        else if (formData[name].length > 128) errors.billingEmail = "Billing Email must be up to 128 characters";
      }
      if (name === "billingMobile" && formData[name]) {
        const phoneError = validatePhoneNumber(formData[name]);
        if (phoneError) errors.billingMobile = phoneError;
      }
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const generateOrderId = async () => {
    const uuid = crypto.randomUUID().replace(/-/g, "").substring(0, 8);
    return `order_${uuid}_${Date.now()}`.substring(0, 40);
  };

  const cleanupExpiredCreditsForUser = async (userId) => {
    try {
      const now = new Date().toISOString();
      const { data: expiredReservations, error: fetchError } = await supabase
        .from("pending_credits_deduction").select("order_id").eq("user_id", userId).lt("expires_at", now);
      if (fetchError) return false;
      if (expiredReservations && expiredReservations.length > 0) {
        const orderIds = expiredReservations.map((r) => r.order_id);
        await supabase.functions.invoke("client-delete-pending-credits-deduction", { body: { order_ids: orderIds } });
      }
      return true;
    } catch (error) {
      console.error("ActivityCheckout.js: Unexpected error cleaning up expired credits:", error);
      return false;
    }
  };

  const generateOrderItems = useCallback(() => {
    const items = [];
    items.push({
      id: `activity_${activity?.id}`,
      type: "Activity",
      details: `${activity?.name || "Activity"}\n${formatActivityDate(activity?.activity_date)}, ${activity?.start_time} - ${activity?.end_time}\n${seats} seat${seats > 1 ? "s" : ""}`,
      detailsForBackend: `${activity?.name || "Activity"} - ${formatActivityDate(activity?.activity_date)} ${activity?.start_time}-${activity?.end_time} x${seats}`,
      price: perSeat * seats,
      basePrice: perSeat * seats,
    });
    if (useCredits && creditsToUse > 0) {
      items.push({ id: "credits", type: "Credits Applied", details: "Credits Used", detailsForBackend: "Credits Used", price: -creditsToUse });
    }
    return items;
  }, [activity, seats, perSeat, useCredits, creditsToUse]);

  const orderItems = useMemo(() => generateOrderItems(), [generateOrderItems]);

  const handleCreditsToggle = () => {
    setUseCredits(!useCredits);
    if (!useCredits) setCreditsToUse(Math.min(availableCredits, Math.max(0, discountedTotal - promoDiscount)));
    else setCreditsToUse(0);
  };

  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) return;
    setIsPromoLoading(true);
    setPromoError('');
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      const response = await fetch(
        'https://api.pickledepot.com.my/functions/v1/validate-promo-code',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'apikey': ANON_KEY },
          body: JSON.stringify({
            code: promoCode.trim(),
            order_amount: discountedTotal,
            booking_type: 'activity_booking',
            sport_name: sportName,
            // NOT read by any deployed function today: validate-promo-code
            // destructures a fixed key set (code/order_amount/booking_type/
            // sport_name/timeslots) and client-create-order-v12 still resolves an
            // activity unit's sport from booking_metadata.sport_name as sent by
            // this page, so a sport-gated promo trusts the client's claim for
            // activities (courts/lessons resolve it from court_id). Sent so a
            // future server-side check can resolve the sport from
            // activities.sport_id without a portal rebuild; extra keys are ignored.
            activity_id: activity?.id ?? null,
            timeslots: activity?.start_time ? [activity.start_time] : [],
            booking_date: activity?.activity_date || null,
          }),
        }
      );
      const data = await response.json();
      if (data.valid) {
        setPromoCodeApplied(data);
        setPromoDiscount(data.discount_amount);
        setPromoError('');
      } else {
        setPromoError(data.error || 'Invalid promo code');
        setPromoCodeApplied(null);
        setPromoDiscount(0);
      }
    } catch (err) {
      setPromoError('Failed to validate promo code. Please try again.');
    } finally {
      setIsPromoLoading(false);
    }
  };

  const handleRemovePromoCode = () => {
    setPromoCode('');
    setPromoCodeApplied(null);
    setPromoDiscount(0);
    setPromoError('');
  };

  const handleCloseNotification = () => setNotification({ message: "", show: false });
  const handlePlaceOrder = (event) => { event.preventDefault(); setShowConfirmationModal(true); };

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    setShowConfirmationModal(false);
    setIsProcessing(true);

    if (phoneInputRef.current) phoneInputRef.current.blur();

    if (!validateForm()) {
      setNotification({ message: "Please fill in all required fields correctly.", show: true });
      setIsProcessing(false);
      return;
    }
    if (!activity || !activity.id) {
      setNotification({ message: "Activity details missing. Please go back and try again.", show: true });
      setIsProcessing(false);
      return;
    }

    const { data: { session: orderSession }, error: orderSessionError } = await supabase.auth.getSession();
    if (orderSessionError || !orderSession) {
      setNotification({ message: "Your session has expired. Please log in again.", show: true });
      setIsProcessing(false);
      navigate("/login", { state: { from: location.pathname, ...memoizedLocationState } });
      return;
    }
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setIsProcessing(false);
      navigate("/login", { state: { from: location.pathname, ...memoizedLocationState } });
      return;
    }

    const orderId = await generateOrderId();
    const sanitizeDescription = (desc) => desc.replace(/[^a-zA-Z0-9\s;-]/g, "").substring(0, 128);
    const billDesc = sanitizeDescription(
      orderItems.map((item) => `${item.type} - ${item.detailsForBackend.replace(/\n/g, "; ")}`).join("; ")
    );

    const bookingMetadata = {
      type: "activity_booking",
      activity_id: activity.id,
      activity_name: activity.name,
      sport_name: sportName,
      activity_date: activity.activity_date,
      start_time: activity.start_time,
      end_time: activity.end_time,
      seats,
      price_per_seat: perSeat,
      credits_to_use: creditsToUse,
    };

    const orderRequestBody = {
      order_id: orderId,
      total_amount: finalTotal,
      status: finalTotal > 0 ? "pending" : "completed",
      billing_name: formData.billingName,
      billing_email: formData.billingEmail,
      billing_mobile: formData.billingMobile.replace(/\s/g, ""),
      billing_address: fixedBillingAddress,
      booking_metadata: bookingMetadata,
    };

    if (finalTotal === 0) {
      // Fully paid with credits — free order endpoint.
      const { data: createFreeOrderResponse, error: createFreeOrderError } =
        await supabase.functions.invoke("client-create-order-free-v8", {
          body: { order_request_body: orderRequestBody, sub_orders: [{ order_id: orderId, booking_metadata: bookingMetadata }], booking_type: "activity_booking", use_credits: useCredits, credits_to_use: creditsToUse },
        });
      if (createFreeOrderError) {
        let errorMessage = "Failed to confirm booking. Please try again.";
        try {
          if (createFreeOrderError.context?.json) {
            const body = await createFreeOrderError.context.json();
            errorMessage = body?.error || errorMessage;
          }
        } catch {}
        setNotification({ message: errorMessage, show: true });
        setIsProcessing(false);
        return;
      }
      if (createFreeOrderResponse && createFreeOrderResponse.success === false) {
        setNotification({ message: createFreeOrderResponse.error || "Failed to confirm booking. Please try again.", show: true });
        setIsProcessing(false);
        return;
      }
      setNotification({ message: "Booking confirmed successfully!", show: true });
      setTimeout(() => navigate("/profile", { state: { selectedProfile: true } }), 2000);
      return;
    }

    // Paid flow.
    if (useCredits && creditsToUse > 0) {
      const cleanupSuccess = await cleanupExpiredCreditsForUser(user.id);
      if (!cleanupSuccess) {
        setNotification({ message: "Failed to clean up expired credit reservations. Please try again or contact support.", show: true });
        setIsProcessing(false);
        return;
      }
    }

    const subOrderId = await generateOrderId();
    const { data: paymentParams, error: createOrderError } = await supabase.functions.invoke(
      "client-create-order-v12",
      { body: {
        main_order: orderRequestBody,
        sub_orders: [{
          order_id: subOrderId,
          parent_order_id: orderId,
          total_amount: finalTotal,
          billing_name: formData.billingName,
          billing_email: formData.billingEmail,
          billing_mobile: formData.billingMobile.replace(/\s/g, ""),
          billing_address: fixedBillingAddress,
          status: "pending",
          booking_metadata: bookingMetadata,
        }],
        ...(promoCodeApplied ? { promo_code: promoCodeApplied.code } : {}),
        ...(promoDiscount > 0 ? { promo_discount_amount: promoDiscount } : {}),
        // Pre-promo, pre-credits subtotal — the same basis validate-promo-code
        // scored at Apply time, so the backend re-check agrees with the preview.
        pre_discount_total: discountedTotal,
      } }
    );

    if (createOrderError) {
      let errorMessage = "Failed to make booking. Please try again or contact support.";
      if (createOrderError.context && createOrderError.context.status) {
        const status = createOrderError.context.status;
        let backendBody = {};
        try { backendBody = await createOrderError.context.json(); } catch {}
        if (status === 400) errorMessage = backendBody?.error || backendBody?.details || "Invalid request details provided.";
        else if (status === 409) errorMessage = backendBody?.error || "This activity is no longer available. Please pick another.";
        else if (status === 500) errorMessage = backendBody?.error || backendBody?.details || "Internal server error during order creation.";
      } else {
        errorMessage = createOrderError.message || errorMessage;
      }
      setNotification({ message: errorMessage, show: true });
      if (useCredits && creditsToUse > 0) {
        await supabase.functions.invoke("client-delete-pending-credits-deduction", { body: { order_ids: [orderId] } });
      }
      setIsProcessing(false);
      return;
    }

    if (paymentFormRef.current) {
      const form = paymentFormRef.current;
      form.querySelector('input[name="merchant_id"]').value = paymentParams.merchant_id;
      form.querySelector('input[name="amount"]').value = paymentParams.amount;
      form.querySelector('input[name="orderid"]').value = paymentParams.order_id;
      form.querySelector('input[name="currency"]').value = paymentParams.currency;
      form.querySelector('input[name="vcode"]').value = paymentParams.vcode;
      form.querySelector('input[name="bill_name"]').value = formData.billingName;
      form.querySelector('input[name="bill_email"]').value = formData.billingEmail;
      form.querySelector('input[name="bill_mobile"]').value = formData.billingMobile.replace(/\s/g, "");
      form.querySelector('input[name="bill_desc"]').value = billDesc;
      form.querySelector('input[name="b_addr1"]').value = fixedBillingAddress;
      form.querySelector('input[name="returnurl"]').value = paymentParams.return_url;
      form.querySelector('input[name="notifyurl"]').value = paymentParams.notify_url;
      form.querySelector('input[name="callbackurl"]').value = paymentParams.callback_url;
      form.querySelector('input[name="metadata"]').value = JSON.stringify({ booking_type: "activity_booking" });
      form.querySelector('input[name="waittime"]').value = paymentParams.waittime;
      form.querySelector('input[name="idempotency_key"]').value = paymentParams.idempotency_key;
      form.querySelector('input[name="guest_checkout"]').value = "0";
      form.submit();
    }
  };

  if (isLoading) return <div className="loading-spinner-container"><div className="loading-spinner"></div></div>;
  if (!isAuthenticated) return null;

  return (
    <div className="checkout-container">
      <Navbar
        selectedSport={sportName}
        selectedBookingType={null}
        onBookingTypeSelect={(type) => navigate("/", { state: { selectedBookingType: type } })}
        onProfileClick={() => navigate("/profile", { state: { selectedProfile: true } })}
        isProfileActive={false}
      />
      <div className="checkout-form">
        <div className="checkout-content">
          <div className="checkout-left">
            <div className="checkout-section">
              <h2 className="checkout-section-header">Billing Information</h2>
              <div className="form-group">
                <label htmlFor="billingName">Full Name <span className="required-marker">*</span></label>
                <input
                  type="text" id="billingName" name="billingName" value={formData.billingName}
                  onChange={(e) => { handleChange(e.target.name, e.target.value); debouncedValidate(e.target.name, e.target.value); }}
                  className="checkout-input" placeholder="Enter your full name" autoComplete="name"
                />
                {formErrors.billingName && <span className="error">{formErrors.billingName}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="billingEmail">Email Address <span className="required-marker">*</span></label>
                <input
                  type="email" id="billingEmail" name="billingEmail" value={formData.billingEmail}
                  onChange={(e) => { handleChange(e.target.name, e.target.value); debouncedValidate(e.target.name, e.target.value); }}
                  className="checkout-input" placeholder="Enter your email" autoComplete="email"
                />
                {formErrors.billingEmail && <span className="error">{formErrors.billingEmail}</span>}
              </div>
              <div className="form-group" ref={phoneInputContainerRef}>
                <label htmlFor="billingMobile">Mobile Number <span className="required-marker">*</span></label>
                <input
                  type="tel" name="billingMobile" id="billingMobile-native" autoComplete="tel"
                  style={{ position: "absolute", opacity: 0, height: 0, pointerEvents: "none" }}
                  onChange={(e) => { handleChange("billingMobile", e.target.value); debouncedValidate("billingMobile", e.target.value); }}
                  value={formData.billingMobile}
                />
                <PhoneInput
                  international value={formData.billingMobile}
                  onChange={(value) => { handleChange("billingMobile", value); debouncedValidate("billingMobile", value); }}
                  onBlur={handlePhoneBlur}
                  placeholder="Enter your phone number (e.g., +12025550123)"
                  inputRef={phoneInputRef}
                  inputProps={{ name: "billingMobile", autoComplete: "tel", type: "tel", id: "billingMobile" }}
                />
                {formErrors.billingMobile && <span className="error">{formErrors.billingMobile}</span>}
              </div>
            </div>
          </div>
          <div className="checkout-right">
            <div className="checkout-section">
              <h2 className="checkout-section-header">Order Summary</h2>
              <div className="order-summary">
                <div className="order-items-scrollable">
                  {orderItems.map((item, index) => (
                    <div className="summary-item" key={index}>
                      <span style={{ whiteSpace: "pre-line" }}>{item.type} - {item.details}</span>
                      <span style={{ whiteSpace: "pre-line" }}>
                        {item.type === "Credits Applied" ? `- RM ${Math.abs(item.price).toFixed(2)}` : `RM ${item.basePrice.toFixed(2)}`}
                      </span>
                    </div>
                  ))}
                  {promoDiscount > 0 && promoCodeApplied && (
                    <div className="summary-item">
                      <span>Promo Code Discount ({promoCodeApplied.code})</span>
                      <span>- RM {promoDiscount.toFixed(2)}</span>
                    </div>
                  )}
                </div>
                <div className="order-totals-static">
                  <div className="promo-code-section">
                    {!promoCodeApplied ? (
                      <div className="promo-code-input-row">
                        <input
                          type="text" className="promo-code-input" placeholder="Enter promo code" value={promoCode}
                          onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(''); }}
                          onKeyDown={(e) => e.key === 'Enter' && handleApplyPromoCode()}
                          disabled={isPromoLoading}
                        />
                        <button className="promo-apply-btn" onClick={handleApplyPromoCode} disabled={isPromoLoading || !promoCode.trim()}>
                          {isPromoLoading ? 'Applying...' : 'Apply'}
                        </button>
                      </div>
                    ) : (
                      <div className="promo-applied-row">
                        <span className="promo-applied-label">
                          Promo: <strong>{promoCodeApplied.code}</strong>
                          {promoCodeApplied.description && <span className="promo-description"> — {promoCodeApplied.description}</span>}
                        </span>
                        <span className="promo-applied-discount">-RM {promoDiscount.toFixed(2)}</span>
                        <button className="promo-remove-btn" onClick={handleRemovePromoCode}>&#x2715;</button>
                      </div>
                    )}
                    {promoError && <p className="promo-error">{promoError}</p>}
                  </div>
                  {availableCredits > 0 && (
                    <div className="credits-section">
                      <label className="credits-label">
                        <input type="checkbox" checked={useCredits} onChange={handleCreditsToggle} />
                        Use Credits (Available: RM {availableCredits.toFixed(2)})
                      </label>
                      {useCredits && (
                        <div className="credits-input-container">
                          <label>Amount to Use:</label>
                          <span className="credits-input" style={{ border: "none", background: "transparent", padding: "8px" }}>
                            RM {creditsToUse.toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="summary-subtotal"><span>Subtotal</span><span>RM {finalTotal.toFixed(2)}</span></div>
                  <div className="summary-subtotal"><span>{SST_LABEL}</span><span>RM {(finalTotal * SST_RATE).toFixed(2)}</span></div>
                  <div className="line-container"><div className="horizontal-line"></div></div>
                  <div className="summary-total"><span>Final Total</span><span>RM {(finalTotal + finalTotal * SST_RATE).toFixed(2)}</span></div>
                </div>
              </div>
              <button onClick={handlePlaceOrder} className="submit-button" aria-label="Place order">Place Order</button>
              {useCredits && creditsToUse > 0 && (
                <p style={{ color: "#d32f2f", fontSize: "0.85rem", marginTop: "10px", textAlign: "center" }}>
                  Note: Credits are held during payment for 3 minutes and will be returned if the payment fails.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      <form ref={paymentFormRef} action="https://pay.fiuu.com/RMS/pay/pickledepot/" method="POST" style={{ display: "none" }} acceptCharset="UTF-8">
        <input type="hidden" name="merchant_id" value="" />
        <input type="hidden" name="amount" value="" />
        <input type="hidden" name="orderid" value="" />
        <input type="hidden" name="currency" value="" />
        <input type="hidden" name="vcode" value="" />
        <input type="hidden" name="bill_name" value="" />
        <input type="hidden" name="bill_email" value="" />
        <input type="hidden" name="bill_mobile" value="" />
        <input type="hidden" name="bill_desc" value="" />
        <input type="hidden" name="b_addr1" value="" />
        <input type="hidden" name="returnurl" value="" />
        <input type="hidden" name="notifyurl" value="" />
        <input type="hidden" name="callbackurl" value="" />
        <input type="hidden" name="metadata" value="" />
        <input type="hidden" name="waittime" value="" />
        <input type="hidden" name="idempotency_key" value="" />
        <input type="hidden" name="guest_checkout" value="0" />
      </form>
      {isProcessing && (
        <div className="modal-overlay" style={{ zIndex: 9999 }}>
          <div className="loading-spinner-container" style={{ background: 'transparent', height: 'auto' }}>
            <div className="loading-spinner"></div>
            <span style={{ marginTop: '15px', color: '#576f3f', fontFamily: '"Manrope", sans-serif', fontSize: '0.95rem' }}>Processing your booking...</span>
          </div>
        </div>
      )}
      {notification.show && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Notification</h2>
            <p>{notification.message}</p>
            <button className="modal-close-button" onClick={handleCloseNotification}>
              <FontAwesomeIcon icon={faTimes} /> Got It
            </button>
          </div>
        </div>
      )}
      <ConfirmationModal isOpen={showConfirmationModal} onClose={() => setShowConfirmationModal(false)} onConfirm={handleSubmit} />
    </div>
  );
};

export default ActivityCheckout;
